import { Box, type DropdownMenuItem, Text } from '@timelyapp/react';
import { InfoIcon } from 'components/Icons';
import { useWithDispatch } from 'hooks/useWithDispatch';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { setTimestampSnappingMinutes as _setTimestampSnappingMinutes } from 'redux/modules/timeline.actions';
import { ALLOWED_TIMESTAMP_SNAPPING_VALUES } from 'redux/modules/timeline.types';
import { getTimestampSnappingMinutes } from 'redux/selectors/timeline';
import styles from './useTimelineSnappingSettings.module.scss';

const useTimelineSnappingSettings = () => {
  const options = ALLOWED_TIMESTAMP_SNAPPING_VALUES;
  const minutes = useSelector(getTimestampSnappingMinutes);
  const value = options.indexOf(minutes);

  const actions = useWithDispatch({
    setTimestampSnappingMinutes: _setTimestampSnappingMinutes,
  });
  const setValue = useCallback(
    (value: number) => {
      actions.setTimestampSnappingMinutes(options[value]);
    },
    [actions, options]
  );
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const index = parseInt(e.target.value, 10);
      setValue(index);
    },
    [setValue]
  );

  const dropdownItem: DropdownMenuItem = useMemo(
    () => ({
      type: 'subMenu',
      label: 'Timestamp intervals',
      icon: <TimelineSnappingIcon />,
      subOptions: [
        {
          type: 'slider',
          label: (
            <Box.Row gap={8} width={250}>
              <span>Timestamp intervals</span>
              <span className={styles.value}>{minutes}min</span>
            </Box.Row>
          ),
          'data-testid': 'timeline_button_options-dropdown_snap-timestamps',
          icon: <TimelineSnappingIcon />,
          // sidenote: `${minutes}m`,
          slider: {
            min: 0,
            max: options.length - 1,
            step: 1,
            value: [value],
            onValueChange: (value: Array<number>) => {
              setValue(value[0]);
            },
          },
        },
        {
          type: 'custom',
          content: (
            <Box.Row className={styles.message} gap={8} alignItems="start">
              <Box.Column flex="1 0 auto">
                <InfoIcon />
              </Box.Column>
              <Box.Column flex="0 1 auto">
                <Text.Paragraph size="small" color="subtle">
                  Dragging timestamps up or down snaps them to the set interval. Clicking a memory
                  adds a timestamp with its start and end time, independent of the interval setting.
                </Text.Paragraph>
              </Box.Column>
            </Box.Row>
          ),
        },
      ],
    }),
    [minutes, options.length, setValue, value]
  );

  return {
    minutes,
    min: 0,
    max: options.length - 1,
    value,
    onChange,
    setValue,
    dropdownItem,
  };
};
export default useTimelineSnappingSettings;

export const TimelineSnappingIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="14"
    height="15"
    viewBox="0 0 14 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    stroke="currentColor"
    {...props}
  >
    <path
      d="M7 11.5L7 3.5M7 11.5L5 10.1667M7 11.5L9 10.1667M7 3.5L5 4.83333M7 3.5L9 4.83333M13 1.5H1M13 13.5H1"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
